'use client';

import { useState, Suspense, useEffect, useRef, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { adoptionInquiryService } from '../../services/adoptionInquiryService';
import { applicationDocumentService } from '../../services/applicationDocumentService';
import { emailNotificationService } from '../../services/emailNotificationService';
import { petService } from '../../utils/petService';
import { CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SlideUp, FadeIn } from '@/components/ui/motion';

interface FormData {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  whatsappNumber: string;
  preferredContact: 'email' | 'phone' | 'both';
  housingType: 'house' | 'apartment' | 'condo' | 'farm' | 'other';
  hasYard: boolean;
  landlordName: string;
  landlordPhone: string;
  landlordEmail: string;
  petPolicyApproved: boolean;
  hasOtherPets: boolean;
  otherPetsDetails: string;
  hasChildren: boolean;
  childrenAges: string;
  experienceLevel: string;
  vetName: string;
  vetPhone: string;
  vetAddress: string;
  employmentStatus: string;
  employerName: string;
  occupation: string;
  workSchedule: string;
  petQuestions: string;
  concerns: string;
  adoptionTimeline: string;
}

function AdoptionApplicationFormContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [petName, setPetName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const petId = searchParams?.get('petId') || '';



  const [formData, setFormData] = useState<FormData>({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    whatsappNumber: '',
    preferredContact: 'email',
    housingType: 'apartment',
    hasYard: false,
    landlordName: '',
    landlordPhone: '',
    landlordEmail: '',
    petPolicyApproved: false,
    hasOtherPets: false,
    otherPetsDetails: '',
    hasChildren: false,
    childrenAges: '',
    experienceLevel: '',
    vetName: '',
    vetPhone: '',
    vetAddress: '',
    employmentStatus: '',
    employerName: '',
    occupation: '',
    workSchedule: '',
    petQuestions: '',
    concerns: '',
    adoptionTimeline: '',
  });



  const loadPetDetails = useCallback(async (signal?: AbortSignal) => {
    try {
      const pet = await petService.getById(petId, signal);
      if (pet && !signal?.aborted) {
        setPetName(pet.name);
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError' && !signal?.aborted) {
        setError('Failed to load pet details');
      }
    }
  }, [petId]);

  useEffect(() => {
    const abortController = new AbortController();

    const initializeForm = async () => {
      // Load pet details if petId exists
      if (petId) {
        await loadPetDetails(abortController.signal);
      }

      // Pre-fill user data if available
      if (user && !abortController.signal.aborted) {
        setFormData(prev => ({
          ...prev,
          contactName: user.user_metadata?.full_name || '',
          contactEmail: user.email || '',
        }));
      }
    };

    initializeForm();

    // Cleanup function to abort ongoing requests
    return () => {
      abortController.abort();
    };
  }, [petId, user, loadPetDetails]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFileUpload = (documentType: string, file: File) => {
    setUploadedFiles(prev => ({
      ...prev,
      [documentType]: file,
    }));
  };

  const removeFile = (documentType: string) => {
    setUploadedFiles(prev => {
      const updated = { ...prev };
      delete updated[documentType];
      return updated;
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.contactName || !formData.contactEmail || !formData.contactPhone || !formData.whatsappNumber) {
          setError('Please fill in all required contact information');
          return false;
        }
        break;
      case 2:
        if (!formData.housingType) {
          setError('Please provide housing information');
          return false;
        }
        if ((formData.housingType === 'apartment' || formData.housingType === 'condo') && !formData.petPolicyApproved) {
          setError('Please confirm pet policy approval');
          return false;
        }
        break;
      case 3:
        if (!formData.experienceLevel) {
          setError('Please provide your pet experience level');
          return false;
        }
        break;
      case 4:
        if (!formData.employmentStatus) {
          setError('Please provide employment information');
          return false;
        }
        break;
      case 5:
        const requiredDocs = ['proof_of_identity', 'proof_of_residence'];
        if (formData.housingType === 'apartment' || formData.housingType === 'condo') {
          requiredDocs.push('other');
        }
        const missingDocs = requiredDocs.filter(doc => !uploadedFiles[doc]);
        if (missingDocs.length > 0) {
          setError(`Please upload required documents: ${missingDocs.join(', ')}`);
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !petId) {
      setError('Please log in to submit an application');
      return;
    }

    if (!validateStep()) return;

    setLoading(true);
    setLoadingMessage('Starting submission...');
    setError('');

    try {
      // 120 second timeout for the critical path (creation + uploads)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Submission timed out. This can happen with large files or slow connections. Please try again or use smaller files.')), 120000);
      });

      await Promise.race([
        (async () => {
          // 1. Create adoption inquiry first
          setLoadingMessage('Creating adoption inquiry...');
          const application = await adoptionInquiryService.create({
            petId,
            userId: user.id,
            ...formData,
          });

          if (!application) {
            throw new Error('Failed to create application - no application returned');
          }

          // 2. Upload documents in parallel
          setLoadingMessage('Uploading required documents...');
          const uploadPromises = Object.entries(uploadedFiles).map(async ([documentType, file]) => {
            const { error: uploadError } = await applicationDocumentService.uploadDocument(
              application.id,
              documentType as any,
              file,
              user.id
            );
            if (uploadError) {
              throw new Error(`Failed to upload ${documentType.replace(/_/g, ' ')}: ${uploadError}`);
            }
          });

          await Promise.all(uploadPromises);

          // 3. Send email notifications (non-blocking for UI success but part of the race)
          setLoadingMessage('Finalizing application...');
          try {
            // Updated to use the correct email service function observed in the original code
            await emailNotificationService.sendApplicationSubmittedEmail(
              application,
              formData.contactEmail,
              formData.contactName,
              petName
            );
          } catch (emailError) {
            console.warn('Failed to send confirmation email:', emailError);
            // Don't fail the whole submission if just the email fails
          }

          console.log('Application submitted successfully');

          setSuccess(true);
          timeoutRef.current = setTimeout(() => {
            router.push(`/application-status-tracking/${application.id}`);
          }, 3000);
        })(),
        timeoutPromise
      ]);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'An error occurred during submission. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  if (!user) {
    return (
      <FadeIn className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please log in to submit an adoption application.</p>
          <button
            onClick={() => router.push('/login')}
            className="w-full bg-primary text-white py-3 rounded-lg hover:bg-secondary transition"
          >
            Log In
          </button>
        </div>
      </FadeIn>
    );
  }

  if (success) {
    return (
      <FadeIn className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full text-center">
          <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h2>
          <p className="text-gray-600 mb-4">
            Thank you for submitting your adoption application for <strong>{petName}</strong>.
          </p>
          <p className="text-gray-600 mb-6">
            We have sent a confirmation email to <strong>{formData.contactEmail}</strong>. Our team will review your application
            and contact you within 2-3 business days.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>What happens next:</strong><br />
              • Application review (2-3 business days)<br />
              • If approved, we will contact you via WhatsApp to schedule an interview<br />
              • You can track your application status in your dashboard
            </p>
          </div>
          <button
            onClick={() => router.push('/pet-discovery')}
            className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-secondary transition"
          >
            Return to Pet Discovery
          </button>
        </div>
      </FadeIn>
    );
  }

  const steps = [
    { number: 1, title: 'Contact Info', description: 'Your contact details' },
    { number: 2, title: 'Housing', description: 'Living situation' },
    { number: 3, title: 'Experience', description: 'Pet care history' },
    { number: 4, title: 'Employment', description: 'Work information' },
    { number: 5, title: 'Documents', description: 'Required files' },
    { number: 6, title: 'Review', description: 'Final check' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-indigo-600 px-8 py-6">
            <h1 className="text-3xl font-bold text-white">Adoption Application</h1>
            <p className="text-blue-100 mt-2">Applying to adopt: <strong>{petName}</strong></p>
          </div>

          <div className="px-8 py-6 border-b">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep >= step.number
                        ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
                        }`}
                    >
                      {currentStep > step.number ? (
                        <CheckCircleIcon className="w-6 h-6" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="text-center mt-2 hidden md:block">
                      <p className="text-xs font-semibold text-gray-900">{step.title}</p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 w-12 md:w-20 mx-2 ${currentStep > step.number ? 'bg-primary' : 'bg-gray-200'
                        }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <XMarkIcon className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {currentStep === 1 && (
              <SlideUp key="step1" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We will contact you via WhatsApp for interview scheduling if your application is approved
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </SlideUp>
            )}

            {currentStep === 2 && (
              <SlideUp key="step2" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Housing Information</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type of Housing <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="housingType"
                    value={formData.housingType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  >
                    <option value="">Select housing type</option>
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="farm">Farm</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="hasYard"
                    checked={formData.hasYard}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-secondary"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Do you have a yard or outdoor space?
                  </label>
                </div>

                {(formData.housingType === 'apartment' || formData.housingType === 'condo') && (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800 font-medium">
                        Landlord information is required for apartment/condo residents
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Landlord Name
                      </label>
                      <input
                        type="text"
                        name="landlordName"
                        value={formData.landlordName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Landlord Phone
                      </label>
                      <input
                        type="tel"
                        name="landlordPhone"
                        value={formData.landlordPhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Landlord Email
                      </label>
                      <input
                        type="email"
                        name="landlordEmail"
                        value={formData.landlordEmail}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="petPolicyApproved"
                        checked={formData.petPolicyApproved}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-secondary"
                        required
                      />
                      <label className="ml-3 text-sm font-medium text-gray-700">
                        I confirm that my landlord has approved pet ownership <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="hasChildren"
                    checked={formData.hasChildren}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-secondary"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Do you have children?
                  </label>
                </div>

                {formData.hasChildren && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ages of Children
                    </label>
                    <input
                      type="text"
                      name="childrenAges"
                      value={formData.childrenAges}
                      onChange={handleInputChange}
                      placeholder="e.g., 5, 8, 12"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    />
                  </div>
                )}
              </SlideUp>
            )}

            {currentStep === 3 && (
              <SlideUp key="step3" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Pet Care Experience</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  >
                    <option value="">Select experience level</option>
                    <option value="first_time">First-time pet owner</option>
                    <option value="beginner">Beginner (1-2 years)</option>
                    <option value="intermediate">Intermediate (3-5 years)</option>
                    <option value="experienced">Experienced (5+ years)</option>
                    <option value="expert">Expert (10+ years)</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="hasOtherPets"
                    checked={formData.hasOtherPets}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-secondary"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Do you currently have other pets?
                  </label>
                </div>

                {formData.hasOtherPets && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please describe your current pets
                    </label>
                    <textarea
                      name="otherPetsDetails"
                      value={formData.otherPetsDetails}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Include species, breeds, ages, and any behavioral notes"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Veterinarian Name
                  </label>
                  <input
                    type="text"
                    name="vetName"
                    value={formData.vetName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Veterinarian Phone
                  </label>
                  <input
                    type="tel"
                    name="vetPhone"
                    value={formData.vetPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Veterinarian Address
                  </label>
                  <textarea
                    name="vetAddress"
                    value={formData.vetAddress}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Questions about this pet
                  </label>
                  <textarea
                    name="petQuestions"
                    value={formData.petQuestions}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Any specific questions or concerns about this pet?"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  />
                </div>
              </SlideUp>
            )}

            {currentStep === 4 && (
              <SlideUp key="step4" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Employment Information</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="employmentStatus"
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                    required
                  >
                    <option value="">Select employment status</option>
                    <option value="full_time">Full-time employed</option>
                    <option value="part_time">Part-time employed</option>
                    <option value="self_employed">Self-employed</option>
                    <option value="retired">Retired</option>
                    <option value="student">Student</option>
                    <option value="unemployed">Unemployed</option>
                  </select>
                </div>

                {formData.employmentStatus && formData.employmentStatus !== 'retired' && formData.employmentStatus !== 'unemployed' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employer Name
                      </label>
                      <input
                        type="text"
                        name="employerName"
                        value={formData.employerName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Occupation/Job Title
                      </label>
                      <input
                        type="text"
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Work Schedule
                      </label>
                      <textarea
                        name="workSchedule"
                        value={formData.workSchedule}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="e.g., Monday-Friday 9AM-5PM, Remote work 3 days per week"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How will you care for the pet while you are at work?
                  </label>
                  <textarea
                    name="concerns"
                    value={formData.concerns}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="e.g., Dog walker available, Pet sitter, Work from home, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    When are you looking to adopt?
                  </label>
                  <select
                    name="adoptionTimeline"
                    value={formData.adoptionTimeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediately">Immediately</option>
                    <option value="1_2_weeks">Within 1-2 weeks</option>
                    <option value="2_4_weeks">Within 2-4 weeks</option>
                    <option value="1_3_months">Within 1-3 months</option>
                    <option value="flexible">Flexible/Just exploring</option>
                  </select>
                </div>
              </SlideUp>
            )}

            {currentStep === 5 && (
              <SlideUp key="step5" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Required Documents</h2>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    Please upload the following documents to complete your application. All documents must be clear and legible.
                  </p>
                </div>

                {[
                  { type: 'proof_of_identity', label: 'Government-Issued ID', required: true },
                  { type: 'proof_of_residence', label: 'Proof of Residence', required: true },
                  { type: 'other', label: 'Landlord Permission Letter', required: formData.housingType === 'apartment' || formData.housingType === 'condo' },
                  { type: 'veterinary_reference', label: 'Previous Pet Veterinary Records', required: false },
                  { type: 'proof_of_income', label: 'Employment Verification', required: false },
                ].map(doc => (
                  <div key={doc.type} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {doc.label}
                          {doc.required && <span className="text-red-500 ml-1">*</span>}
                        </h3>
                        {uploadedFiles[doc.type] && (
                          <p className="text-sm text-gray-600 mt-1">
                            {uploadedFiles[doc.type].name}
                          </p>
                        )}
                      </div>
                      {uploadedFiles[doc.type] && (
                        <button
                          type="button"
                          onClick={() => removeFile(doc.type)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    {!uploadedFiles[doc.type] && (
                      <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-secondary transition">
                        <div className="text-center">
                          <DocumentArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                          <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (Max 10MB)</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 10 * 1024 * 1024) {
                                setError('File size must be less than 10MB');
                                return;
                              }
                              handleFileUpload(doc.type, file);
                              setError('');
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                ))}
              </SlideUp>
            )}

            {currentStep === 6 && (
              <SlideUp key="step6" className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Review Your Application</h2>

                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
                    <p className="text-sm text-gray-600">Name: {formData.contactName}</p>
                    <p className="text-sm text-gray-600">Email: {formData.contactEmail}</p>
                    <p className="text-sm text-gray-600">Phone: {formData.contactPhone}</p>
                    <p className="text-sm text-gray-600">WhatsApp: {formData.whatsappNumber}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Housing</h3>
                    <p className="text-sm text-gray-600">Type: {formData.housingType}</p>
                    <p className="text-sm text-gray-600">Has Yard: {formData.hasYard ? 'Yes' : 'No'}</p>
                    {formData.landlordName && (
                      <p className="text-sm text-gray-600">Landlord: {formData.landlordName}</p>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Experience</h3>
                    <p className="text-sm text-gray-600">Level: {formData.experienceLevel}</p>
                    <p className="text-sm text-gray-600">Has Other Pets: {formData.hasOtherPets ? 'Yes' : 'No'}</p>
                    {formData.vetName && (
                      <p className="text-sm text-gray-600">Veterinarian: {formData.vetName}</p>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Employment</h3>
                    <p className="text-sm text-gray-600">Status: {formData.employmentStatus}</p>
                    {formData.employerName && (
                      <p className="text-sm text-gray-600">Employer: {formData.employerName}</p>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Documents Uploaded</h3>
                    <ul className="list-disc list-inside text-sm text-gray-600">
                      {Object.keys(uploadedFiles).map(type => (
                        <li key={type}>{type.replace(/_/g, ' ')}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>By submitting this application, you confirm that:</strong>
                  </p>
                  <ul className="list-disc list-inside text-sm text-yellow-800 mt-2 space-y-1">
                    <li>All information provided is accurate and truthful</li>
                    <li>You understand that false information may result in application denial</li>
                    <li>You agree to a home visit if required</li>
                    <li>You will provide proper care and veterinary attention to the adopted pet</li>
                  </ul>
                </div>
              </SlideUp>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={loading}
                  className="flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ArrowLeftIcon className="w-5 h-5 mr-2" />
                  Previous
                </button>
              )}

              {currentStep < 6 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={loading}
                  className="ml-auto flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="ml-auto flex items-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? (loadingMessage || 'Submitting...') : 'Submit Application'}
                  <CheckCircleIcon className="w-5 h-5 ml-2" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdoptionApplicationForm() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application form...</p>
        </div>
      </div>
    }>
      <AdoptionApplicationFormContent />
    </Suspense>
  );
}